import { EReservesMonitoringTab } from '@/types/routes';
import { IStablecoinReportDto, IStablecoinReserveDto } from '@/api/stablecoin/stablecoin.types';

export enum EReportCadence {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
}

export enum EFinancialEstimatesSection {
  ASSETS_AND_LIABILITIES = 'assets-and-liabilities',
  EQUITY_BUFFER = 'equity-buffer',
  ESTIMATED_REVENUE = 'estimated-revenue',
  ASSET_BREAKDOWN = 'asset-breakdown',
  LIQUIDITY_RATIOS = 'liquidity-ratios',
}

export enum EOnChainFlowsSeries {
  ISSUANCES = 'issuances',
  REDEMPTIONS = 'redemptions',
  NET_ISSUANCE = 'net-issuance',
}

export type TReportedAssetField =
  | 'tbills'
  | 'repo'
  | 'termRepo'
  | 'cash'
  | 'mmf'
  | 'commercialPaper'
  | 'gold'
  | 'btc'
  | 'securedLoans'
  | 'corporateBonds'
  | 'publicEquities'
  | 'other';

export interface IStablecoinConfig {
  name: string;
  token: string;
  reportCadence: EReportCadence;
  tabs: EReservesMonitoringTab[];
  financialEstimatesSections: EFinancialEstimatesSection[];
  hasHoldingsOverlay: boolean;
  reserveCompositionKeys?: (keyof IStablecoinReserveDto)[];
  reportedAssetKeys?: (keyof IStablecoinReportDto)[];
  onChainFlowsSeries?: EOnChainFlowsSeries[];
}

// Stablecoins with a dedicated details view, keyed by agioId
export const STABLECOIN_CONFIG = {
  tether: {
    name: 'Tether International, S.A. DE C.V.',
    token: 'USDT',
    reportCadence: EReportCadence.QUARTERLY,
    tabs: [
      EReservesMonitoringTab.OVERVIEW,
      EReservesMonitoringTab.REPORTED_FIGURES,
      EReservesMonitoringTab.FINANCIAL_ESTIMATES,
      EReservesMonitoringTab.ON_CHAIN_FLOWS,
      EReservesMonitoringTab.STRESS_TESTING,
      EReservesMonitoringTab.MARKET_PRICES,
    ],
    financialEstimatesSections: [
      EFinancialEstimatesSection.ASSETS_AND_LIABILITIES,
      EFinancialEstimatesSection.EQUITY_BUFFER,
      EFinancialEstimatesSection.ASSET_BREAKDOWN,
      EFinancialEstimatesSection.LIQUIDITY_RATIOS,
    ],
    hasHoldingsOverlay: true,
  },
  circle: {
    name: 'Circle Internet Group, Inc.',
    token: 'USDC',
    reportCadence: EReportCadence.MONTHLY,
    tabs: [
      EReservesMonitoringTab.OVERVIEW,
      EReservesMonitoringTab.REPORTED_FIGURES,
      EReservesMonitoringTab.FINANCIAL_ESTIMATES,
      EReservesMonitoringTab.ON_CHAIN_FLOWS,
      EReservesMonitoringTab.MARKET_PRICES,
    ],
    financialEstimatesSections: [
      EFinancialEstimatesSection.ASSETS_AND_LIABILITIES,
      EFinancialEstimatesSection.EQUITY_BUFFER,
      EFinancialEstimatesSection.ESTIMATED_REVENUE,
      EFinancialEstimatesSection.ASSET_BREAKDOWN,
    ],
    hasHoldingsOverlay: false,
    reserveCompositionKeys: ['overnightRepoMusd', 'tbillsMusd', 'cashMusd'],
    reportedAssetKeys: ['tbills', 'repo', 'cash'],
    onChainFlowsSeries: [EOnChainFlowsSeries.NET_ISSUANCE],
  },
} satisfies Record<string, IStablecoinConfig>;

export type TSupportedStablecoinKey = keyof typeof STABLECOIN_CONFIG;

export const SUPPORTED_STABLECOINS = Object.keys(STABLECOIN_CONFIG) as TSupportedStablecoinKey[];

export const isSupportedStablecoin = (agioId: string): agioId is TSupportedStablecoinKey => {
  return SUPPORTED_STABLECOINS.includes(agioId as TSupportedStablecoinKey);
};

export const getStablecoinConfig = (agioId: string): IStablecoinConfig | undefined =>
  isSupportedStablecoin(agioId) ? STABLECOIN_CONFIG[agioId] : undefined;
